import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import ModuleBar from './ModuleBar';
import Breadcrumbs from './Breadcrumbs';
import BottomTabBar from './BottomTabBar';
import MenuSearch from './MenuSearch';
import NotificationToaster from '@/components/common/NotificationToaster';
import PageHelp from '@/components/common/PageHelp';
import { findScreenGuide } from '@/lib/screenGuide';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { useMenu } from '@/context/MenuContext';
import { useTheme } from '@/context/ThemeContext';

const PageFallback = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-9 w-9 animate-spin rounded-full border-4 border-[var(--brand)] border-t-transparent" />
  </div>
);

/**
 * AppShell — 상단 유틸바(Navbar) + 모듈바(GNB) + 사이드바(LNB) + 본문 + 모바일 하단탭.
 *
 * 이전 버전과의 차이:
 *  - window.innerWidth 1회성 판정 → useIsDesktop(matchMedia) 로 리사이즈 대응
 *  - GNB / Breadcrumb / 모바일 하단탭 / 메뉴검색(Ctrl+K) 추가
 *  - 라우트 lazy 로딩을 위한 Suspense 경계 제공
 */
const MainLayout = () => {
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const { breadcrumb } = useMenu();
  const { syncFromServer } = useTheme();

  // 로그인이 끝난 뒤에야 계정에 저장된 테마를 읽을 수 있다(그 전에는 401).
  useEffect(() => { syncFromServer(); }, [syncFromServer]);

  // 지금 화면의 설명. 표에 없으면 단추를 그리지 않는다.
  const guide = findScreenGuide(location.pathname);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // 페이지를 이동하거나 데스크톱/모바일이 전환되면 모바일 사이드바를 닫는다.
  // (효과 대신 렌더 중 상태 조정 — 열린 서랍이 한 프레임 남아 보이는 깜빡임이 없다)
  const layoutKey = `${location.pathname}|${isDesktop}`;
  const [lastLayoutKey, setLastLayoutKey] = useState(layoutKey);
  if (lastLayoutKey !== layoutKey) {
    setLastLayoutKey(layoutKey);
    if (isSidebarOpen) setIsSidebarOpen(false);
  }

  // Ctrl+K / Cmd+K 로 메뉴 검색
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Navbar 의 햄버거 버튼: 데스크톱은 접기, 모바일은 열기/닫기
  const toggleSidebar = useCallback(() => {
    if (isDesktop) setIsCollapsed((prev) => !prev);
    else setIsSidebarOpen((prev) => !prev);
  }, [isDesktop]);

  const toggleCollapse = useCallback(() => setIsCollapsed((prev) => !prev), []);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-app">
      {/* 상단 유틸리티 바 (알림 / 사용자) */}
      <Navbar toggleSidebar={toggleSidebar} />

      {/* GNB — 모듈 스위처 (데스크톱) */}
      <ModuleBar />

      <div className="flex min-h-0 flex-1">
        {/* LNB */}
        <Sidebar
          isOpen={isSidebarOpen}
          isCollapsed={isDesktop ? isCollapsed : false}
          toggleSidebar={toggleSidebar}
          toggleCollapse={toggleCollapse}
          onOpenSearch={() => setIsSearchOpen(true)}
        />

        {/* 본문 — 사이드바와 맞닿는 좌측 위 모서리를 둥글려 한 장의 패널처럼 보이게 한다 */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden lg:rounded-tl-lg lg:border-l lg:border-t lg:border-app-border">
          {/*
            경로 표시줄. 오른쪽 끝에 화면 설명 단추를 둔다.

            설명을 화면 파일마다 넣지 않고 여기 한 곳에서 띄운다 — 화면이
            83개라 심는 방식으로는 빠진 곳이 생기고, 자리도 제각각이 된다.
            내용은 lib/screenGuide.jsx 한 표에 모여 있다.
          */}
          {breadcrumb.length > 0 && (
            <div className="flex shrink-0 items-center justify-between gap-3 rounded-tl-lg border-b border-app-border bg-surface px-4 py-2 lg:px-6">
              <Breadcrumbs />
              {guide && <PageHelp title={guide.title}>{guide.body}</PageHelp>}
            </div>
          )}

          <main className="flex-1 overflow-y-auto overflow-x-hidden bg-app p-4 pb-20 lg:p-6 lg:pb-6">
            <div className="container mx-auto">
              <Suspense fallback={<PageFallback />}>
                <Outlet />
              </Suspense>
            </div>
          </main>
        </div>
      </div>

      {/* 모바일 사이드바 오버레이 */}
      {isSidebarOpen && !isDesktop && (
        <div
          className="fixed inset-0 z-40 bg-black/55 lg:hidden"
          onClick={toggleSidebar}
          role="presentation"
        />
      )}

      {/* 모바일 하단 탭바 */}
      <BottomTabBar />

      {/* 메뉴 검색 (Ctrl+K) — 열릴 때만 마운트해 상태를 자연스럽게 초기화한다 */}
      {isSearchOpen && <MenuSearch onClose={() => setIsSearchOpen(false)} />}

      {/* 새 알림을 우측 상단 팝업으로 띄운다(화면은 그리지 않는다) */}
      <NotificationToaster />
    </div>
  );
};

export default MainLayout;
